// ** Polyfills
import ResizeObserver from "resize-observer-polyfill";

// ** React Imports
import { useState, useContext, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

// ** Third Party Components
import { useLayer } from "react-laag";
import useVH from "react-viewport-height";
import { Swiper, SwiperSlide } from "swiperNew/react";
import { Zoom } from "swiperNew/modules";

// ** Custom Components
import Icon from "components/global/Icon";
import { StyledOverlay } from "styles/components/Overlay.styled";

// ** Utils & Helpers
import cdnPrefixer from "helpers/cdnPrefixer";
import api from "helpers/ApiClient";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// ** Styled Components
import {
	StyledModalWrapper,
	StyledModalComponent,
	StyledModalMainSection,
	StyledCloseButton,
} from "components/layout/Modal";
import {
	StyledAttachmentDialogPicture,
	StyledAttachmentDialogBlock,
} from "../styles";

// ** SVG Static Imports
import { ReactComponent as LightTimes } from "resources/icons/light/times.svg";

// #####################################################

const AttachmentModal = ({ isOpen, close, open }) => {
	const [locationKeys, setLocationKeys] = useState([]);
	const [attachmentPicture, setAttachmentPicture] = useState(false);

	const history = useHistory();
	const location = useLocation();
	const { isPhone, isTablet } = useContext(deviceTypeContext);

	// ** Funkcja aktualizujaca wysokość okna przegladarki mobilnej 'vh'
	// ** w zalezności od tego czy pasek adresu jest pokazany czy ukryty
	useVH();

	const closeModal = () => {
		setAttachmentPicture(null);
		document.body.style.overflow = "unset";
		close();
	};

	const handleZoomChange = (swiper, scale) => {
		if (scale < 1) {
			swiper.zoom.set(1);
		}
	};

	// ** Hook useLayer - React Laag
	const { renderLayer, triggerProps, layerProps } = useLayer({
		onOutsideClick: () => {
			if (history.location.state) {
				history.goBack();
			} else {
				history.replace(history.location.pathname);
			}
			closeModal();
		},
		isOpen,
		overflowContainer: true,
		auto: false,
		placement: "bottom-start",
		triggerOffset: 0,
		container: "app",
		ResizeObserver,
	});

	// ** Efekty zamykający modal po kliknięciu 'wstecz'
	useEffect(() => {
		return history.listen((location) => {
			if (history.action === "PUSH") {
				setLocationKeys([location.key]);
			}

			if (history.action === "POP") {
				if (locationKeys[1] === location.key) {
					setLocationKeys(([_, ...keys]) => keys);
				} else {
					closeModal();
				}
			}
		});
	}, [locationKeys]);

	useEffect(() => {
		if (location.hash.match("#attachment/[0-9]+")) {
			if (location.state?.data?.file_big) {
				setAttachmentPicture(location.state?.data?.file_big || false);
				open();
				document.body.style.overflow = "hidden";
			} else {
				api.get("/message/attachment", {
					params: { id: location.hash.split("/")[1] },
				})
					.then(({ data }) => {
						const { file_big } = data || {};
						setAttachmentPicture(file_big);
						open();
						document.body.style.overflow = "hidden";
					})
					.catch(() => {
						closeModal();
					});
			}
		} else if (isOpen && !location.hash.match("#attachment/[0-9]+")) {
			closeModal();
		}
	}, [location.hash, location.pathname]);

	// #####################################################

	return (
		<>
			{/* Funkcja Renderująca Aktywator */}
			<div {...triggerProps}></div>

			{/* Funkcja Renderująca Modal */}
			{renderLayer(
				<>
					{/* Wyświetlanie ciemniejszego tła w czasie otwarcia dropdowna na urządzeniu mobilnym */}
					{isOpen && (
						<StyledModalWrapper>
							{!isPhone && !isTablet && (
								<StyledCloseButton
									label="Close"
									onClick={() => {
										if (history.location.state) {
											history.goBack();
										} else {
											history.replace(
												history.location.pathname
											);
										}
										closeModal();
									}}
								>
									<Icon
										svgIconComponent={LightTimes}
										icon="times"
										type="fal"
									/>
								</StyledCloseButton>
							)}
							<StyledModalComponent ref={layerProps.ref}>
								{/* Wyświetlanie przycisku 'Close' na Mobile */}
								{(isPhone || isTablet) && (
									<StyledCloseButton
										label="Close"
										onClick={() => {
											if (history.location.state) {
												history.goBack();
											} else {
												history.replace(
													history.location.pathname
												);
											}
											closeModal();
										}}
									>
										<Icon
											svgIconComponent={LightTimes}
											icon="times"
											type="fal"
										/>
									</StyledCloseButton>
								)}
								<StyledModalMainSection>
									<StyledAttachmentDialogBlock>
										<Swiper
											style={{
												width: "100%",
												height: "100%",
											}}
											className="carouselSwiper noselect"
											modules={[Zoom]}
											zoom
											onResize={(swiper) => {
												swiper.update();
											}}
											onZoomChange={handleZoomChange}
										>
											<SwiperSlide>
												{attachmentPicture && (
													<div
														style={{
															display: "flex",
															alignItems:
																"center",
															justifyContent:
																"center",
															height: "100%",
															width: "100%",
														}}
														className="swiper-zoom-container"
													>
														<StyledOverlay
															style={{
																zIndex: 1001,
															}}
														/>
														<StyledAttachmentDialogPicture
															src={cdnPrefixer(
																attachmentPicture
															)}
															alt="big"
														/>
													</div>
												)}
											</SwiperSlide>
										</Swiper>
									</StyledAttachmentDialogBlock>
								</StyledModalMainSection>
							</StyledModalComponent>
						</StyledModalWrapper>
					)}
				</>
			)}
		</>
	);
};

// #####################################################

export default AttachmentModal;

// #####################################################
